import React from 'react'
import PropTypes from 'prop-types'
import { connect as connectRedux } from 'react-redux'
import _isFunction from 'lodash/isFunction'
import _partial from 'lodash/partial'
import ImmutablePropTypes from 'react-immutable-proptypes'
import { getBoundActions } from '../../actions/index'
import { truncate } from '../../theme/web-app/index'
import { upstreamContextOnClick as upstreamContextClick } from '../../services/upstream-context/index'
import HeroImage from '../HeroImage/index'
import { TARGET_BLANK } from '../Link/index'
import { H1, HEADING_TYPES } from '../Heading/index'
import Button, { TYPES } from '../Button/index'
import Picture, { IMAGE_TYPE_WEBP, IMAGE_LOADING_LAZY } from '../Picture/index'
import {
  getOptimizedSpotlightImages,
  getPromoButtonText,
  getPromoButtonLink,
} from '../Spotlight/utils'

function SpotlightPromoBannerV2 ({
  data,
  label,
  upstreamContext,
  setUpstreamContext,
  viewportWidth,
  staticText,
}) {
  const smallUrl = data.getIn(['heroImage', 'small'])
  const mediumSmallUrl = data.getIn(['heroImage', 'mediumSmall'])
  const mediumUrl = data.getIn(['heroImage', 'medium'])
  const largeUrl = data.getIn(['heroImage', 'large'])
  const hasImage = smallUrl || mediumSmallUrl || mediumUrl || largeUrl
  const title = data.get('title')
  const teaser = data.get('teaser')
  const buttonLink = getPromoButtonLink(data)
  const buttonText = getPromoButtonText(data, staticText)
  const image = data.get('largeKeyArtImage')
  const imageUrls = getOptimizedSpotlightImages({ url: image }) || {}
  const {
    fallbackImageUrl,
    smallImageUrl,
    mediumImageUrl,
  } = imageUrls

  const getUpstreamContextOnClick = () => {
    return _partial(
      upstreamContextClick,
      _partial.placeholder,
      { upstreamContext, setUpstreamContext },
    )
  }

  const handleClick = (e, onClick) => {
    if (_isFunction(onClick)) onClick(e)
  }

  const upstreamContextOnClick = getUpstreamContextOnClick()

  const bannerClassName = [
    'spotlight-promo-banner-v2',
    hasImage ? null : 'spotlight-promo-banner-v2--default-bg',
  ].filter((className) => !!className).join(' ')

  return (
    <div className={bannerClassName}>
      {hasImage
        ? <>
            {
              viewportWidth <= 767
                ? <Picture
                    fallbackSrc={fallbackImageUrl}
                    width={'608'}
                    height={'342'}
                    alt={data.get('title')}
                    className="spotlight-promo-banner-v2__mobile-hero"
                    imageClassName="spotlight-promo-banner-v2__mobile-hero-img"
                    loading={IMAGE_LOADING_LAZY}
                    sources={[
                      {
                        type: IMAGE_TYPE_WEBP,
                        srcSet: smallImageUrl,
                        media: '(max-width: 480px)',
                      },
                      {
                        type: IMAGE_TYPE_WEBP,
                        srcSet: mediumImageUrl,
                        media: '(max-width: 768px)',
                      },
                    ]}
                  />
                : <HeroImage
                    className={['spotlight-promo-banner-v2__background-hero']}
                    alwaysUseProps
                    smallUrl={smallUrl}
                    mediumSmallUrl={mediumSmallUrl}
                    mediumUrl={mediumUrl}
                    largeUrl={largeUrl}
                    url={largeUrl}
                  />
            }
          </>
        : null
      }
      <div className="spotlight-banner-v2__overlay" />
      <div className="spotlight-banner-v2__overlay-darken" />
      <div className="spotlight-promo-banner-v2__content">
        { label
          ? <div className="spotlight-promo-banner-v2__label-container">
              <span className="spotlight-promo-banner-v2__label">
                { label }
              </span>
            </div>
          : null
        }
        <H1
          as={HEADING_TYPES.H4}
          inverted
          className="spotlight-promo-banner-v2__title"
        >
          {title}
        </H1>
        {teaser
          ? <p className="spotlight-promo-banner-v2__teaser">{truncate(teaser, 300)}</p>
          : null
        }
        {buttonLink && buttonText
          ? <Button
              type={TYPES.PRIMARY}
              url={buttonLink}
              scrollToTop
              onClick={(e) => handleClick(e, upstreamContextOnClick)}
              className="spotlight-promo-banner-v2__cta"
              target={TARGET_BLANK}
              directLink
            >
            {buttonText}
          </Button>
          : null
        }
      </div>
    </div>
  )
}

SpotlightPromoBannerV2.propTypes = {
  staticText: ImmutablePropTypes.map.isRequired,
  viewportWidth: PropTypes.number,
  useGraphQLData: PropTypes.bool,
}

export default connectRedux(
  (state) => {
    return {
      viewportWidth: state.app.getIn(['viewport', 'width'], 0),
      staticText: state.staticText.getIn(['data', 'spotlightPromoBanner', 'data']),
    }
  },
  (dispatch) => {
    const actions = getBoundActions(dispatch)
    return {
      setUpstreamContext: actions.upstreamContext.setUpstreamContext,
    }
  },
)(SpotlightPromoBannerV2)
